import { Injectable } from '@angular/core';

export interface SavedFilterState {
  selectedJobtitle: string | null;
  selectedLob: string | null;
  selectedStartdate: Date | string | null;
  selectedRaisedBy: string | null;
  selectedLeader: string | null;
  selectedRaisedOn: Date | string | null;
  selectedLocation: string | null;
  selectedStatus: string | null;
  selectedSharedByVendor: string | null;
  timestamp: number;
}

export interface SavedCandidateFilterState {
  selectedName: string | null;
  selectedExperience: string | null;
  selectedLocation: string | null;
  selectedStatus: string | null;
  selectedAvailableBy: Date | string | null;
  selectedDateApplied: Date | string | null;
  selectedHiringManager: string | null;
  selectedPosition: string | null;
   selectedVendor: string | null;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class FilterPersistenceService {
  private readonly STORAGE_KEY_PREFIX = 'open_position_filters_';
  private readonly CANDIDATE_STORAGE_KEY_PREFIX = 'candidate_filters_';
  private readonly DEFAULT_EXPIRY_DAYS = 30;

  constructor() {}

  saveFilters(personaType: string, filterState: Partial<SavedFilterState>): void {
    try {
      const storageKey = this.getStorageKey(personaType);
      const dataToSave: SavedFilterState = {
        ...filterState,
        timestamp: Date.now()
      } as SavedFilterState;
      
      localStorage.setItem(storageKey, JSON.stringify(dataToSave));
    } catch (error) {
      console.warn('Failed to save filters to localStorage:', error);
    }
  }

  loadFilters(personaType: string): SavedFilterState | null {
    try {
      const storageKey = this.getStorageKey(personaType);
      const savedData = localStorage.getItem(storageKey);
      
      if (!savedData) {
        return null;
      }

      const parsedData: SavedFilterState = JSON.parse(savedData);
      
      if (this.isExpired(parsedData.timestamp)) {
        this.clearFilters(personaType);
        return null;
      }

      return parsedData;
    } catch (error) {
      console.warn('Failed to load filters from localStorage:', error);
      return null;
    }
  }

  clearFilters(personaType: string): void {
    try {
      const storageKey = this.getStorageKey(personaType);
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.warn('Failed to clear filters from localStorage:', error);
    }
  }

  hasFilters(personaType: string): boolean {
    try {
      const storageKey = this.getStorageKey(personaType);
      const savedData = localStorage.getItem(storageKey);
      
      if (!savedData) {
        return false;
      }

      const parsedData: SavedFilterState = JSON.parse(savedData);
      return !this.isExpired(parsedData.timestamp);
    } catch (error) {
      return false;
    }
  }

  getAllSavedPersonaTypes(): string[] {
    const savedTypes: string[] = [];
    
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.STORAGE_KEY_PREFIX)) {
          const personaType = key.replace(this.STORAGE_KEY_PREFIX, '');
          savedTypes.push(personaType);
        }
      }
    } catch (error) {
      console.warn('Failed to get saved persona types:', error);
    }

    return savedTypes;
  }

  clearAllFilters(): void {
    try {
      const keysToRemove: string[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith(this.STORAGE_KEY_PREFIX) || key.startsWith(this.CANDIDATE_STORAGE_KEY_PREFIX))) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.warn('Failed to clear all filters:', error);
    }
  }

  saveCandidateFilters(personaType: string, filterState: Partial<SavedCandidateFilterState>): void {
    try {
      const storageKey = this.getCandidateStorageKey(personaType);
      const dataToSave: SavedCandidateFilterState = {
        ...filterState,
        timestamp: Date.now()
      } as SavedCandidateFilterState;
      
      localStorage.setItem(storageKey, JSON.stringify(dataToSave));
    } catch (error) {
      console.warn('Failed to save candidate filters to localStorage:', error);
    }
  }

  loadCandidateFilters(personaType: string): SavedCandidateFilterState | null {
    try {
      const storageKey = this.getCandidateStorageKey(personaType);
      const savedData = localStorage.getItem(storageKey);
      
      if (!savedData) {
        return null;
      }

      const parsedData: SavedCandidateFilterState = JSON.parse(savedData);
      
      if (this.isExpired(parsedData.timestamp)) {
        this.clearCandidateFilters(personaType);
        return null;
      }

      return parsedData;
    } catch (error) {
      console.warn('Failed to load candidate filters from localStorage:', error);
      return null;
    }
  }

  clearCandidateFilters(personaType: string): void {
    try {
      const storageKey = this.getCandidateStorageKey(personaType);
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.warn('Failed to clear candidate filters from localStorage:', error);
    }
  }

  hasCandidateFilters(personaType: string): boolean {
    try {
      const storageKey = this.getCandidateStorageKey(personaType);
      const savedData = localStorage.getItem(storageKey);
      
      if (!savedData) {
        return false;
      }

      const parsedData: SavedCandidateFilterState = JSON.parse(savedData);
      return !this.isExpired(parsedData.timestamp);
    } catch (error) {
      return false;
    }
  }

  private getCandidateStorageKey(personaType: string): string {
    return `${this.CANDIDATE_STORAGE_KEY_PREFIX}${personaType}`;
  }

  private getStorageKey(personaType: string): string {
    return `${this.STORAGE_KEY_PREFIX}${personaType}`;
  }

  private isExpired(timestamp: number): boolean {
    const expiryTime = this.DEFAULT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    return (Date.now() - timestamp) > expiryTime;
  }
}
